import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';
import { ModulesService } from '@shared/services/modules.service';

import { Router } from '@angular/router';
// Material
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
// Services & Models
import { PermissionsService } from '@shared/services/permissions.service';
import {
  Permission,
  CreatePermissionPayload,
  UpdatePermissionPayload,
} from '@shared/models/entities/permission.model';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
//Utils
import { sortAlphabetically } from '@shared/utils/sort.utils';
import { PermissionFormDialogComponent } from './permission-form-dialog/permission-form-dialog.component';

import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { SimpleExcelService } from '@shared/services/simple-excel.service';
@Component({
  selector: 'app-permissions',
  standalone: true,
  imports: [ MatButtonToggleModule, MatCheckboxModule, SelectionBarComponent,PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDialogModule
],
  templateUrl: './permissions.component.html',
  styleUrl: './permissions.component.scss',
})
export class PermissionsComponent implements OnInit {
  /** Vista tabla / tarjetas y selección con checkbox (misma que en catálogos). */
  readonly listView = new ListView('permissions', 'table');
  readonly sel = new ListSelection<any>(() => (this.dataSource?.filteredData ?? []) as any[]);
  private readonly excelSvc = inject(SimpleExcelService);

  /** Exporta a Excel la selección. */
  async exportExcel(rows: any[]): Promise<void> {
    if (!rows?.length) return;
    await this.excelSvc.simpleXLSX({
      fileName: 'Permisos',
      sheets: [{
        sheetName: 'Permisos'.slice(0, 31), header: [], lineBreak: 0,
        columns: [
          { columnKey: 'name', columnName: 'Nombre' },
          { columnKey: 'description', columnName: 'Descripción' },
          { columnKey: '_active', columnName: 'Estado' },
        ],
        data: rows.map((r: any) => ({ ...r, _active: r.active ? 'Activo' : 'Inactivo' })),
      }],
    } as any);
  }

  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/permissions', 'Permisos');


  private readonly permissionsService   = inject(PermissionsService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly router               = inject(Router);
  private readonly dialog               = inject(MatDialog);

  // ─── Estado ────────────────────────────────────────────────────────────────
  loading    = this.permissionsService.loading;
  searchText = signal('');

  // ─── Tabla ─────────────────────────────────────────────────────────────────
  columns: string[]  = ['select', 'name', 'description', 'active', 'actions'];
  dataSource         = new MatTableDataSource<Permission>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['permissions'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadPermissions()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.loadPermissions();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Alta / edición: en diálogo compartido (FormDialogShell) — título y
  // acciones fijos, contenido scrolleable — en vez del panel inline.
  // ─────────────────────────────────────────────────────────────────────────────

  openCreate(): void {
    this.openFormDialog();
  }

  openEdit(perm: Permission): void {
    this.openFormDialog(perm);
  }

  private openFormDialog(permission?: Permission): void {
    const ref = this.dialog.open(PermissionFormDialogComponent, {
      data: { permission },
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().subscribe(saved => {
      if (saved) this.loadPermissions();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────────

  async loadPermissions(): Promise<void> {
    try {
      await this.permissionsService.search({
        searchType: 'default',
        condition: { deleted: false },
      });
      this.dataSource.data = sortAlphabetically(this.permissionsService.listData(), 'name');
      this.dataSource.paginator = this.paginator;
      this.applyFilter(this.searchText());
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar los permisos.',
        dismissBtn: true,
      });
    }
  }

  async toggleActive(perm: Permission): Promise<void> {
    try {
      await this.permissionsService.toggleActive(perm.id, !perm.active);
      await this.loadPermissions();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo cambiar el estado.',
        dismissBtn: true,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Búsqueda
  // ─────────────────────────────────────────────────────────────────────────────

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText.set(value);
    this.applyFilter(value);
  }

  private applyFilter(value: string): void {
    this.dataSource.filter = value.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  // ─── Helpers template ───────────────────────────────────────────────────────

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }
}